import { MAX_TEMP, MIN_TEMP } from "@qendo/profile/journal";
import {
    type ExtraReportsData,
    type PointData,
    type RecordDataNumber,
    type ReportablePeriod,
    type ReportableScale,
    ReportableScales,
    type ReportableTrackable,
    type ReportColourProps,
    ReportingPeriod,
    YAxisScale,
    YAxisScaleMap,
} from "@qendo/profile/reports";
import type { ThemeColours } from "@qendo/theme";
import { type MeasurableTrackableType, MeasurableTrackableTypes, Mood, type TrackableData } from "@qendo/trackables";
import {
    type DateArg,
    isFuture,
    isThisMonth,
    isThisYear,
    startOfMonth,
    startOfWeek,
    startOfYear,
    toDate,
} from "date-fns";
import { filled, nullish } from "~/util";
import { formatDateString } from "~/util/dates";
import { formatNumeric, type Numeric, type NumericString, parseNumber } from "~/util/string";

export function isReportable(trackable?: TrackableData | null): trackable is ReportableTrackable {
    return (
        filled(trackable) &&
        MeasurableTrackableTypes.includes(trackable.type as MeasurableTrackableType) &&
        ReportableScales.includes(trackable.scale as ReportableScale)
    );
}

export function formatAverage(average?: Numeric | null): NumericString {
    return formatNumeric(average ?? 0, {
        style: "decimal",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }) as NumericString;
}

export function getMinYAxis(scale: ReportableScale, points?: PointData[] | null): number {
    switch (YAxisScaleMap[scale]) {
        case YAxisScale.mood:
            return Mood.very_sad;
        case YAxisScale.temperature:
            return MIN_TEMP;
        case YAxisScale.water:
            return Math.max(0, ...(points ?? []).map(({ average }) => Math.floor(parseNumber(average) / 5) * 5 - 5));
        default:
            return Math.max(0, ...(points ?? []).map(({ average }) => Math.floor(parseNumber(average) / 10) * 10 - 10));
    }
}

export function getMaxYAxis(scale: ReportableScale, points?: PointData[] | null): number {
    switch (YAxisScaleMap[scale]) {
        case YAxisScale.mood:
            return Mood.very_happy;
        case YAxisScale.temperature:
            return MAX_TEMP;
        case YAxisScale.water:
            return Math.max(5, ...(points ?? []).map(({ average }) => Math.ceil(parseNumber(average) / 5) * 5));
        default:
            return Math.max(10, ...(points ?? []).map(({ average }) => Math.ceil(parseNumber(average) / 10) * 10));
    }
}

export function getNumberOfSections(scale: ReportableScale, min: number, max: number): number {
    switch (YAxisScaleMap[scale]) {
        case YAxisScale.mood:
            return 4;
        case YAxisScale.temperature:
            return Math.floor(MAX_TEMP - MIN_TEMP);
        case YAxisScale.water:
            return Math.max(5, Math.floor(max - min));
        default:
            return Math.max(7, Math.floor(max - min));
    }
}

export function extractColours(theme: ThemeColours, extras?: ExtraReportsData | null): string[] {
    return [
        theme.reports[0],
        ...(extras ?? []).map((_r, idx) => theme.reports[(idx + 1) % theme.reports.length]),
    ].slice(0, 5);
}

export function reportColourProps(theme: ThemeColours, extras: ExtraReportsData): ReportColourProps {
    return Object.fromEntries(
        extractColours(theme, extras).flatMap((colour, index) => {
            const key = (index + 1) as RecordDataNumber;

            return [
                [`color${key}`, colour],
                [`dataPointsColor${key}`, colour],
            ];
        }),
    );
}

export function extractReportData(points: PointData[], labels?: string[] | Record<string & DateArg<Date>, string>) {
    return points.map(({ date, average }, idx, array) => {
        const steps = Math.floor(array.length / 7);

        const label = Array.isArray(labels) ? labels[idx] : labels?.[formatDateString(date)];
        const value = parseNumber(average);
        const showVerticalLine = idx && idx % steps === 0;

        return {
            value,
            label,
            showVerticalLine,
            hideDataPoint: nullish(average),
        };
    });
}

export function extractLineSegments(points: PointData[]) {
    return points
        .map(({ average }, idx, array) => {
            return {
                startIndex: idx,
                endIndex: idx + 1,
                color: nullish(average) || nullish(array[idx + 1]?.average) ? "transparent" : undefined,
            };
        })
        .slice(0, -1);
}

export function resolveDateForPeriod(period: ReportablePeriod, date?: DateArg<Date> | null): DateArg<Date> | null {
    if (date && period === ReportingPeriod.yearly && !isThisYear(date) && !isFuture(date)) {
        return formatDateString(startOfYear(toDate(date)));
    } else if (date && period === ReportingPeriod.monthly && !isThisMonth(date) && !isFuture(date)) {
        return formatDateString(startOfMonth(toDate(date)));
    } else if (date && period === ReportingPeriod.weekly && !isFuture(date)) {
        return formatDateString(startOfWeek(toDate(date)));
    }

    return null;
}
